import { CanvasTexture, LinearMipmapLinearFilter, RepeatWrapping, SRGBColorSpace, type Texture } from 'three';
import { HEX, mixCss } from './palette';

/**
 * Everything with lettering is drawn into canvases at startup with the bundled
 * fonts (no image files). Flat palette colours only; the paper grain overlay is
 * the one true "texture".
 */
export const FONT_TITLE = "'Bodoni Moda', 'Didot', 'Bodoni 72', serif";
export const FONT_BLOCK = "'Archivo Black', 'Arial Black', sans-serif";
/** The poster lettering of the menus (the house poster uses it too). */
export const FONT_DISPLAY = "'League Gothic', 'Oswald', 'Arial Narrow', sans-serif";
export const FONT_TEXT = "'Barlow Condensed', 'Arial Narrow', sans-serif";

export async function loadFonts(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  await Promise.all([
    document.fonts.load(`italic 700 64px 'Bodoni Moda'`),
    document.fonts.load(`italic 900 64px 'Bodoni Moda'`),
    document.fonts.load(`400 64px 'Archivo Black'`),
    document.fonts.load(`400 64px 'League Gothic'`),
    document.fonts.load(`500 64px 'Barlow Condensed'`),
    document.fonts.load(`600 64px 'Barlow Condensed'`),
    document.fonts.load(`700 64px 'Barlow Condensed'`),
  ]).catch(() => undefined);
}

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  return [c, ctx];
}

function tex(c: HTMLCanvasElement): CanvasTexture {
  const t = new CanvasTexture(c);
  t.colorSpace = SRGBColorSpace;
  t.anisotropy = 4;
  t.minFilter = LinearMipmapLinearFilter;
  return t;
}

/** Number roundel: cream disc (or `bg`: the champion's gold), dark-brown number. */
export function roundelTexture(num: string, bg: string = HEX.cream): CanvasTexture {
  const [c, g] = canvas(256, 256);
  g.fillStyle = bg;
  g.fillRect(0, 0, 256, 256);
  g.fillStyle = HEX.brown;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `${num.length > 1 ? 126 : 172}px ${FONT_BLOCK}`;
  g.fillText(num, 128, num.length > 1 ? 136 : 140);
  return tex(c);
}

// ---------------------------------------------------------------------------
// Sponsor boards (invented brands only)
// ---------------------------------------------------------------------------

export interface Atlas {
  texture: Texture;
  count: number;
  /** [u0, v0, u1, v1] of slot i. */
  uv(i: number): [number, number, number, number];
}

type Draw = (g: CanvasRenderingContext2D, w: number, h: number) => void;

const SPONSORS: Draw[] = [
  // BELMONTE: red board, cream oval
  (g, w, h) => {
    g.fillStyle = HEX.red;
    g.fillRect(0, 0, w, h);
    g.fillStyle = HEX.cream;
    g.beginPath();
    g.ellipse(w / 2, h / 2, w * 0.42, h * 0.36, 0, 0, Math.PI * 2);
    g.fill();
    text(g, 'BELMONTE', w / 2, h / 2 + 4, `${h * 0.36}px ${FONT_BLOCK}`, HEX.brown, w * 0.74);
  },
  // SERRAVIA: ochre board, slanted red type with a swoosh
  (g, w, h) => {
    g.fillStyle = HEX.ochre;
    g.fillRect(0, 0, w, h);
    g.save();
    g.translate(w / 2, h / 2);
    g.transform(1, -0.08, -0.25, 1, 0, 0);
    text(g, 'SERRAVIA', 0, -4, `${h * 0.42}px ${FONT_BLOCK}`, HEX.red, w * 0.8);
    g.restore();
    g.strokeStyle = HEX.red;
    g.lineWidth = h * 0.06;
    g.beginPath();
    g.moveTo(w * 0.18, h * 0.8);
    g.quadraticCurveTo(w * 0.5, h * 0.62, w * 0.86, h * 0.7);
    g.stroke();
  },
  // RIVOLTA: cypress-green board, cream type, a wave
  (g, w, h) => {
    g.fillStyle = HEX.cypress;
    g.fillRect(0, 0, w, h);
    text(g, 'RIVOLTÀ', w / 2, h * 0.44, `${h * 0.42}px ${FONT_BLOCK}`, HEX.cream, w * 0.78);
    g.strokeStyle = HEX.cream;
    g.lineWidth = h * 0.05;
    g.beginPath();
    for (let x = w * 0.12; x <= w * 0.88; x += 4) g.lineTo(x, h * 0.8 + Math.sin(x / 18) * h * 0.05);
    g.stroke();
  },
  // ORSOLINA caffe: cream board, brown border, red Bodoni
  (g, w, h) => {
    g.fillStyle = HEX.cream;
    g.fillRect(0, 0, w, h);
    g.strokeStyle = HEX.brown;
    g.lineWidth = h * 0.08;
    g.strokeRect(h * 0.08, h * 0.08, w - h * 0.16, h - h * 0.16);
    text(g, 'Caffè Orsolina', w / 2, h / 2 + 2, `italic 900 ${h * 0.4}px ${FONT_TITLE}`, HEX.red, w * 0.8);
  },
  // TORRENTI gomme: dark board, ochre type
  (g, w, h) => {
    g.fillStyle = HEX.brown;
    g.fillRect(0, 0, w, h);
    text(g, 'TORRENTI', w / 2, h * 0.42, `${h * 0.4}px ${FONT_BLOCK}`, HEX.ochre, w * 0.76);
    text(g, 'GOMME DA CORSA', w / 2, h * 0.78, `${h * 0.15}px ${FONT_BLOCK}`, HEX.cream, w * 0.6);
  },
  // SERENELLA acqua: olive board, cream italic
  (g, w, h) => {
    g.fillStyle = HEX.olive;
    g.fillRect(0, 0, w, h);
    text(g, 'Acqua Serenella', w / 2, h / 2 + 2, `italic 700 ${h * 0.42}px ${FONT_TITLE}`, HEX.cream, w * 0.84);
  },
  // VALDORA olio: terracotta board
  (g, w, h) => {
    g.fillStyle = HEX.terracotta;
    g.fillRect(0, 0, w, h);
    g.fillStyle = HEX.cream;
    g.beginPath();
    g.arc(w * 0.16, h / 2, h * 0.3, 0, Math.PI * 2);
    g.fill();
    text(g, 'VALDORA', w * 0.58, h * 0.44, `${h * 0.38}px ${FONT_BLOCK}`, HEX.cream, w * 0.62);
    text(g, 'OLIO MOTORE', w * 0.58, h * 0.78, `${h * 0.14}px ${FONT_BLOCK}`, HEX.brown, w * 0.5);
  },
  // CORVINO aperitivo: cream board, red block type, brown band
  (g, w, h) => {
    g.fillStyle = HEX.cream;
    g.fillRect(0, 0, w, h);
    g.fillStyle = HEX.brown;
    g.fillRect(0, h * 0.78, w, h * 0.22);
    text(g, 'CORVINO', w / 2, h * 0.42, `${h * 0.44}px ${FONT_BLOCK}`, HEX.red, w * 0.8);
    text(g, 'APERITIVO', w / 2, h * 0.9, `${h * 0.13}px ${FONT_BLOCK}`, HEX.cream, w * 0.4);
  },
];

function text(g: CanvasRenderingContext2D, s: string, x: number, y: number, font: string, color: string, maxW: number): void {
  g.font = font;
  g.fillStyle = color;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(s, x, y, maxW);
}

function atlasOf(draws: Draw[], slotW: number, slotH: number, cols: number): Atlas {
  const rows = Math.ceil(draws.length / cols);
  const [c, g] = canvas(slotW * cols, slotH * rows);
  draws.forEach((d, i) => {
    const x = (i % cols) * slotW;
    const y = Math.floor(i / cols) * slotH;
    g.save();
    g.translate(x, y);
    g.beginPath();
    g.rect(0, 0, slotW, slotH);
    g.clip();
    d(g, slotW, slotH);
    g.restore();
  });
  const t = tex(c);
  return {
    texture: t,
    count: draws.length,
    uv: (i) => {
      const cx = i % cols;
      const cy = Math.floor(i / cols);
      // Inset half a texel-ish to avoid bleeding.
      const u0 = (cx * slotW + 2) / c.width;
      const u1 = ((cx + 1) * slotW - 2) / c.width;
      const v1 = 1 - (cy * slotH + 2) / c.height;
      const v0 = 1 - ((cy + 1) * slotH - 2) / c.height;
      return [u0, v0, u1, v1];
    },
  };
}

export function sponsorAtlas(): Atlas {
  return atlasOf(SPONSORS, 512, 128, 2);
}

/** 150 / 100 / 50 braking boards: cream with dark-brown numerals. */
export function brakingBoardAtlas(): Atlas {
  const draw = (label: string): Draw => (g, w, h) => {
    g.fillStyle = HEX.cream;
    g.fillRect(0, 0, w, h);
    text(g, label, w / 2, h / 2 + h * 0.04, `${h * 0.62}px ${FONT_BLOCK}`, HEX.brown, w * 0.86);
  };
  return atlasOf([draw('150'), draw('100'), draw('50')], 256, 160, 1);
}

// ---------------------------------------------------------------------------
// House poster for the billboard and the blimp (Phase 3: the winner's image)
// ---------------------------------------------------------------------------

export function posterTexture(title: string, dateLabel: string): CanvasTexture {
  const W = 1024;
  const H = 640;
  const [c, g] = canvas(W, H);
  const pad = 26;
  g.fillStyle = HEX.cream;
  g.fillRect(0, 0, W, H);
  g.save();
  g.beginPath();
  g.rect(pad, pad, W - pad * 2, H - pad * 2);
  g.clip();
  // Sky bands.
  const bands = [HEX.ochre, mixCss('ochre', 'terracotta', 0.2), mixCss('ochre', 'terracotta', 0.4)];
  bands.forEach((col, i) => {
    g.fillStyle = col;
    g.fillRect(0, 0, W, H * (0.62 - i * 0.16));
  });
  g.fillStyle = HEX.cream;
  g.beginPath();
  g.arc(W * 0.74, H * 0.3, H * 0.15, 0, Math.PI * 2);
  g.fill();
  // Hills.
  const hill = (base: number, amp: number, col: string, ph: number): void => {
    g.fillStyle = col;
    g.beginPath();
    g.moveTo(0, H);
    for (let x = 0; x <= W; x += 8) g.lineTo(x, H * base - Math.sin(x / 140 + ph) * amp - Math.sin(x / 61 + ph * 2) * amp * 0.3);
    g.lineTo(W, H);
    g.fill();
  };
  hill(0.6, 26, mixCss('asphalt', 'cream', 0.35), 1.2);
  hill(0.7, 30, HEX.olive, 3.1);
  hill(0.82, 22, HEX.grass, 0.4);
  // Cypress silhouettes.
  g.fillStyle = HEX.cypress;
  for (const [x, hgt] of [[0.1, 150], [0.14, 110], [0.86, 170], [0.9, 120], [0.94, 140]] as const) {
    g.beginPath();
    g.ellipse(W * x, H * 0.8 - hgt / 2, 18, hgt / 2, 0, 0, Math.PI * 2);
    g.fill();
  }
  // Road sweeping in.
  g.fillStyle = HEX.asphalt;
  g.beginPath();
  g.moveTo(W * 0.3, H);
  g.quadraticCurveTo(W * 0.52, H * 0.84, W * 0.62, H * 0.72);
  g.lineTo(W * 0.66, H * 0.72);
  g.quadraticCurveTo(W * 0.7, H * 0.86, W * 0.78, H);
  g.fill();
  // Halftone print dots.
  g.fillStyle = 'rgba(59, 42, 30, 0.08)';
  for (let y = 0; y < H; y += 9) for (let x = (y / 9) % 2 ? 4 : 0; x < W; x += 9) {
    g.beginPath();
    g.arc(x, y, 1.6, 0, Math.PI * 2);
    g.fill();
  }
  g.restore();
  // Lettering.
  text(g, 'grand premio', W / 2, H * 0.2, `400 ${H * 0.25}px ${FONT_DISPLAY}`, HEX.red, W * 0.86);
  text(g, title.toLowerCase(), W / 2, H * 0.37, `400 ${H * 0.095}px ${FONT_DISPLAY}`, HEX.brown, W * 0.8);
  text(g, dateLabel.toUpperCase(), W / 2, H - pad - 34, `700 ${H * 0.05}px ${FONT_TEXT}`, HEX.cream, W * 0.6);
  return tex(c);
}

// ---------------------------------------------------------------------------
// Paper grain (the one overlay texture)
// ---------------------------------------------------------------------------

export function paperGrainTexture(size = 512): CanvasTexture {
  const [c, g] = canvas(size, size);
  const img = g.createImageData(size, size);
  // Value noise: a few octaves of smooth random + per-pixel fleck.
  const rnd = mulberry(0x9e3779b9);
  const octaves = [16, 64, 256];
  const grids = octaves.map((n) => {
    const a = new Float32Array((n + 1) * (n + 1));
    for (let i = 0; i < a.length; i++) a[i] = rnd();
    // Make it tile: last row/col = first.
    for (let i = 0; i <= n; i++) {
      a[i * (n + 1) + n] = a[i * (n + 1)];
      a[n * (n + 1) + i] = a[i];
    }
    return { n, a };
  });
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let v = 0;
      const amps = [0.22, 0.34, 0.44];
      let oi = 0;
      for (const { n, a } of grids) {
        const amp = amps[oi++];
        const fx = (x / size) * n;
        const fy = (y / size) * n;
        const ix = Math.floor(fx);
        const iy = Math.floor(fy);
        const tx = fx - ix;
        const ty = fy - iy;
        const sx = tx * tx * (3 - 2 * tx);
        const sy = ty * ty * (3 - 2 * ty);
        const i00 = a[iy * (n + 1) + ix];
        const i10 = a[iy * (n + 1) + ix + 1];
        const i01 = a[(iy + 1) * (n + 1) + ix];
        const i11 = a[(iy + 1) * (n + 1) + ix + 1];
        v += amp * (i00 + (i10 - i00) * sx + (i01 - i00) * sy + (i00 - i10 - i01 + i11) * sx * sy);
      }
      v = v + (rnd() - 0.5) * 0.45;
      const k = (y * size + x) * 4;
      const g8 = Math.max(0, Math.min(255, Math.round(v * 255)));
      img.data[k] = img.data[k + 1] = img.data[k + 2] = g8;
      img.data[k + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  // Paper fibres.
  g.globalAlpha = 0.18;
  for (let i = 0; i < 900; i++) {
    const x = rnd() * size;
    const y = rnd() * size;
    const a = rnd() * Math.PI;
    const l = 4 + rnd() * 14;
    g.strokeStyle = rnd() < 0.5 ? '#ffffff' : '#000000';
    g.lineWidth = 0.6;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l);
    g.stroke();
  }
  const t = new CanvasTexture(c);
  t.wrapS = RepeatWrapping;
  t.wrapT = RepeatWrapping;
  return t;
}

function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
